import React, { useMemo, useState } from "react";

const THEMES = {
  modern: {
    name: "modern",
    bodyFont: "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    displayFont: "Sora, Inter, ui-sans-serif",
    bg: "#0b0e12",
    text: "#e6edf3",
    subtext: "#9aa5af",
    accent: "#8bd3dd",
    border: "#242424",
    cardBg: "#151515",
    pillBg: "#121212",
    link: "#8bd3dd",
  },
  sims: {
    name: "sims",
    bodyFont: "OPTICivet, Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    displayFont: "OPTICivet, Sora, Inter, ui-sans-serif",
    bg: "linear-gradient(180deg, #cfefff 0%, #e9fbff 100%)",
    text: "#183153",
    subtext: "#466d8c",
    accent: "#39b54a",
    border: "#9bd6ff",
    cardBg: "#ffffff",
    pillBg: "#f2fbff",
    link: "#1aa7ec",
  },
};

function Section({ title, children, theme }) {
  const id = `${title.replace(/\s+/g, "-").toLowerCase()}-title`;
  return (
    <section style={{ marginTop: 56 }} aria-labelledby={id}>
      <h2 id={id} style={{ fontFamily: theme.displayFont, fontSize: 28, fontWeight: 700, marginBottom: 16, borderBottom: `2px solid ${theme.accent}`, display: "inline-block" }}>{title}</h2>
      {children}
    </section>
  );
}

function Pill({ children, theme }) {
  return (
    <span style={{ display: "inline-block", border: `1px solid ${theme.border}`, padding: "6px 10px", borderRadius: 999, fontSize: 12, color: theme.subtext, background: theme.pillBg }}>{children}</span>
  );
}

function ExperienceItem({ company, role, location, dates, points, theme }) {
  return (
    <article
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 8,
        padding: 16,
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        background: theme.cardBg,
        transition: "transform 0.25s ease, background 0.25s ease, box-shadow 0.25s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.05)";
        e.currentTarget.style.background =
          theme.name === "sims" ? "rgba(255, 255, 255, 0.9)" : "#1b1b1b";
        e.currentTarget.style.boxShadow =
          theme.name === "sims"
            ? "0 6px 20px rgba(57, 181, 74, 0.25)"
            : "0 6px 20px rgba(139, 211, 221, 0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.background = theme.cardBg;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 600 }}>{company}</div>
        <div style={{ color: theme.subtext }}>{location}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ color: theme.text }}>{role}</div>
        <div style={{ color: theme.subtext }}>{dates}</div>
      </div>
      <ul style={{ margin: 0, paddingLeft: 20, color: theme.text, lineHeight: 1.6 }}>
        {points.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>
    </article>
  );
}

function ProjectCard({ title, subtitle, href, image, stats, theme }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" style={{ textDecoration: "none", color: "inherit" }} aria-label={`${title} project link`}>
    <div
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        overflow: "hidden",
        background: theme.cardBg,
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-6px) scale(1.05)";
        e.currentTarget.style.boxShadow = theme.name === "sims"
          ? "0 6px 18px rgba(57, 181, 74, 0.25)"
          : "0 6px 18px rgba(139, 211, 221, 0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0) scale(1)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >       
<img src={image} alt={`${title} cover`} style={{ width: "100%", height: 180, objectFit: "cover", filter: "none" }} />
        <div style={{ padding: 14 }}>
          <div style={{ fontWeight: 600 }}>{title}</div>
          <div style={{ color: theme.subtext, fontSize: 14, marginTop: 4 }}>{subtitle}</div>
          {stats ? <div style={{ color: theme.accent, fontSize: 13, marginTop: 8 }}>{stats}</div> : null}
        </div>
      </div>
    </a>
  );
}

export default function App() {
  const [mode, setMode] = useState("modern");
  const theme = useMemo(() => THEMES[mode], [mode]);
  const toggleLabel = mode === "modern" ? "Switch to Sims 1 Inspired Theme" : "Switch to Modern";

  return (
    <div style={{ fontFamily: theme.bodyFont, background: theme.bg, color: theme.text, minHeight: "100vh" }}>
      
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="/fonts/OPTICivet.otf" rel="stylesheet" />
      <a href="#content" style={{ position: "absolute", left: -9999, top: 0 }} onFocus={(e) => { e.currentTarget.style.left = "12px"; e.currentTarget.style.top = "12px"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#000"; e.currentTarget.style.padding = "8px 12px"; e.currentTarget.style.borderRadius = "8px"; }}>Skip to content</a>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "32px 20px 56px" }}>
        <header role="banner" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontFamily: theme.displayFont, fontSize: 34, letterSpacing: mode === "sims" ? 0 : 0.2 }}>Siam Shafiq</h1>
            <p style={{ margin: "6px 0 0", color: theme.subtext }}>Software Engineer + Aspiring Gamedev</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <nav aria-label="Primary">
              <ul style={{ display: "flex", gap: 16, listStyle: "none", margin: 0, padding: 0 }}>
                <li><a href="#experience" style={{ color: theme.text, textDecoration: "none" }}>Experience</a></li>
                <li><a href="#projects" style={{ color: theme.text, textDecoration: "none" }}>Projects</a></li>
                <li><a href="#hackathons" style={{ color: theme.text, textDecoration: "none" }}>Hackathons</a></li>
                <li><a href="#skills" style={{ color: theme.text, textDecoration: "none" }}>Skills</a></li>
                <li><a href="#education" style={{ color: theme.text, textDecoration: "none" }}>Education</a></li>
                <li><a href="#contact" style={{ color: theme.text, textDecoration: "none" }}>Contact</a></li>
              </ul>
            </nav>
            <button
              onClick={() => setMode(mode === "modern" ? "sims" : "modern")}
              aria-pressed={mode === "sims"}
              aria-label={toggleLabel}
              style={{
                cursor: "pointer",
                background: mode === "sims" ? theme.accent : "transparent",
                color: mode === "sims" ? "#fff" : theme.accent,
                border: `1px solid ${theme.accent}`,
                padding: "8px 12px",
                borderRadius: 8,
                fontFamily: theme.displayFont,
              }}
            >
              {toggleLabel}
            </button>

            <a
              href="/siam_shafiq_resume.pdf"
              download
              style={{
                cursor: "pointer",
                background: theme.accent,
                color: theme.name === "sims" ? "#fff" : "#000",
                border: `1px solid ${theme.accent}`,
                padding: "8px 12px",
                borderRadius: 8,
                fontFamily: theme.displayFont,
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              Download Résumé
            </a>

          </div>
        </header>

        <main id="content" role="main">
          <Section title="Summary" theme={theme}>
            <p style={{ color: theme.text, maxWidth: 1080 }}>I'm a software engineer with a focus on C++, C and Java. In my previous co-ops I worked with a startup that designed drones for non-destructive testing. I was primarily tasked with developing a lidar-based mapping system which involved writing firmware in C and designing a friendly user interface for clients. Last summer I worked with Circuitstream teaching a cohort of kids about machine learning and AI in Python. I recently completed the EA software engineering virtual experience where I proposed and implemented gameplay feature improvements for EA Sports College Football. In my own time I've also built a Stardew Valley inspired 'Guess Who' game playable on the browser and I've also implemented some playable demos of features I wondered about in video games such as Minecraft.</p>
          </Section>

          <div id="experience"></div>
          <Section title="Work & Additional Experiences" theme={theme}>
            <div style={{ display: "grid", gap: 16 }}>
              <ExperienceItem
                company="Electronic Arts (EA) — Software Engineering Virtual Experience (Forage)"
                role="Participant"
                location="Remote"
                dates="Oct 2025"
                points={[
                  "Proposed a new feature for EA Sports College Football with stakeholder-ready write-up.",
                  "Produced a class diagram and C++ header file for core objects.",
                  "Patched a bug and optimized codepaths by introducing an improved data structure.",
                ]}
                theme={theme}
              />
              <ExperienceItem
                company="Circuitstream"
                role="AI and Machine Learning Instructor"
                location="Vancouver, BC"
                dates="Jul 2024 – Aug 2024"
                points={[
                  "Led courses; mentored students through OpenAI-powered Discord bots in Python.",
                  "Developed lessons on AI algorithms, ML models, and recommender systems.",
                  "Provided 1:1 coaching to strengthen coding proficiency and problem-solving.",
                ]}
                theme={theme}
              />
              <ExperienceItem
                company="Avestec Technologies"
                role="Software Engineer"
                location="Burnaby, BC"
                dates="May 2023 – Oct 2023"
                points={[
                  "Delivered ~20% performance boost in telemetry software for better real-time responsiveness.",
                  "Built LiDAR-based 3D mapping to process spatial data in real time for object detection and reconstruction.",
                  "Integrated multi-sensor inputs with C++ and built backend visualization tools.",
                  "Engineered embedded C firmware to interface with DFT sensors for accurate measurements.",
                ]}
                theme={theme}
              />
              <ExperienceItem
                company="UBC Robotics"
                role="Full Stack Web Developer"
                location="Vancouver, BC"
                dates="Feb 2023 – Sep 2023"
                points={[
                  "Improved site load times by ~15% by optimizing oversized assets.",
                  "Implemented full UI overhaul with a responsive Tailwind-based design.",
                  "Completed feasibility study for a modular user-profile system to support growth.",
                ]}
                theme={theme}
              />
            </div>
          </Section>

          <div id="projects"></div>
          <Section title="Projects" theme={theme}>
            <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              <ProjectCard title="Minecraft Block Placing Demo" subtitle="Simple Minecraft-style block placing demo built in Unity (C#)" href="https://siamshafiq.com/minecraft/index.html" image="/images/minecraft.avif" theme={theme} />
              <ProjectCard title="Blink demo" subtitle="Demonstrating a simple blink in Unity" href="https://siamshafiq.com/BlinkDemo/index.html" image="/images/blink.png" theme={theme} />
              <ProjectCard title="Low res graphics demo" subtitle="Low res texture that a lot of games appreciate" href="https://siamshafiq.com/LowPolyDemo/index.html" image="/images/lowpoly.png" theme={theme} />
              <ProjectCard title="Magnetize Demo" subtitle="Magnetism in Unity!" href="https://siamshafiq.com/Magnetize/index.html" image="/images/magnet.png" theme={theme} />
              <ProjectCard title="Shooting Demo" subtitle="Oh Shoot!" href="https://siamshafiq.com/ShootDemo/index.html" image="/images/shoot.png" theme={theme} />
              <ProjectCard title="Stardew Valley — Guess Who" subtitle="Classic Guess Who reimagined; HTML/CSS/JS; Firebase analytics" href="https://siamshafiq.github.io/Guess-Who-Stardew/" image="images/stardew-game.png" stats="25,000+ users" theme={theme} />
              <ProjectCard title="Pokemon TCG Card Finder" subtitle="Finds and displays Pokemon cards using the PokemonTCG API" href="https://siamshafiq.com/pokemontcg/index.html" image="/images/pokemon-tcg.png" theme={theme} />
              <ProjectCard title="Promptly — Write Everyday" subtitle="Minimalist writing platform; Netlify deploy; grew engagement via UBC Canvas & Reddit" href="https://writepromptly.netlify.app/" image="images/promptly.png" theme={theme} />
            </div>
          </Section>

          <div id="hackathons"></div>
          <Section title="Hackathons" theme={theme}>
            <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              <ProjectCard
                title="CUThere"
                subtitle="Hackathon: cuHacking — location-based social app for meeting nearby people"
                href="https://github.com/SiamShafiq/CUThere"
                image="/images/cuHacking.png"
                theme={theme}
              />
              <ProjectCard
                title="HomeSafe"
                subtitle="Hackathon: PolyHacks — safety-aware routing with crowd feedback and route news"
                href="https://github.com/SiamShafiq/HomeSafe"
                image="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTH55I73uWFYvEWL7NMQnj6mwgSvIhvuAk5VQ&s"
                theme={theme}
              />
            </div>
          </Section>

          <div id="skills"></div>
          <Section title="Skills" theme={theme}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxWidth: 900 }}>
              <Pill theme={theme}>C</Pill>
              <Pill theme={theme}>C++</Pill>
              <Pill theme={theme}>Python</Pill>
              <Pill theme={theme}>JavaScript</Pill>
              <Pill theme={theme}>React</Pill>
              <Pill theme={theme}>Git</Pill>
              <Pill theme={theme}>Linux</Pill>
              <Pill theme={theme}>Embedded</Pill>
              <Pill theme={theme}>LiDAR</Pill>
              <Pill theme={theme}>Data Structures</Pill>
              <Pill theme={theme}>Algorithms</Pill>
              <Pill theme={theme}>Performance</Pill>
              <Pill theme={theme}>SQL</Pill>
              <Pill theme={theme}>APIs</Pill>
            </div>
          </Section>

          <div id="education"></div>
          <Section title="Education" theme={theme}>
            <div style={{ display: "grid", gap: 16 }}>
              <article style={{ display: "grid", gap: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                  <div style={{ fontWeight: 600 }}>University of British Columbia</div>
                  <div style={{ color: theme.subtext }}>Vancouver, BC</div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                  <div style={{ color: theme.text }}>B.A. in Philosophy</div>
                  <div style={{ color: theme.subtext }}>Sept 2022 – Graduating Dec 2025</div>
                </div>
                <div style={{ color: theme.subtext, fontSize: 14 }}>Relevant: PHIL 220 (Logic), PHIL 250 (AI & Cognition), WRDS 350, INFO 100, ASTR 310</div>
              </article>
              <article style={{ display: "grid", gap: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                  <div style={{ fontWeight: 600 }}>Langara College</div>
                  <div style={{ color: theme.subtext }}>Vancouver, BC</div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                  <div style={{ color: theme.text }}>Associate Degree in Computer Science</div>
                  <div style={{ color: theme.subtext }}>May 2019 – Sept 2022</div>
                </div>
                <div style={{ color: theme.subtext, fontSize: 14 }}>Relevant: CPSC 110, CPSC 210, COSC 111, COSC 114, COSC 122, MATH 100, PHYS 117</div>
              </article>
            </div>
          </Section>

          <div id="contact"></div>
          <Section title="Contact" theme={theme}>
            <p style={{ color: theme.text }}>
              <a href="mailto:siam.shafiq@gmail.com" style={{ color: theme.link, textDecoration: "none" }}>siam.shafiq@gmail.com</a> · <a href="https://siamshafiq.com" target="_blank" rel="noreferrer" style={{ color: theme.link, textDecoration: "none" }}>siamshafiq.com</a><br />
              <span style={{ color: theme.subtext }}>Burnaby, British Columbia</span>
            </p>
          </Section>
        </main>

        <footer role="contentinfo" style={{ textAlign: "center", marginTop: 56, color: theme.subtext, fontSize: 14 }}>
          <p>© {new Date().getFullYear()} Siam Shafiq</p>
        </footer>
      </div>
    </div>
  );
}
